export const SLOPE_BANDS = [
  { lo: 0,  hi: 3,  label: '0–3°'   }, // Flat — feeding/staging
  { lo: 3,  hi: 8,  label: '3–8°'   }, // Gentle — travel corridors
  { lo: 8,  hi: 16, label: '8–16°'  }, // Moderate — transition habitat
  { lo: 16, hi: 26, label: '16–26°' }, // Significant — bedding terrain
  { lo: 26, hi: 35, label: '26–35°' }, // Steep — security cover
  { lo: 35, hi: 45, label: '35–45°' }, // Very steep — escape terrain
  { lo: 45, hi: 90, label: '45°+'   }, // Cliff/bluff
]

export const ASPECT_DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export const SOLID_COLORS = ['#589500', '#FF3B30', '#00C7FF', '#FFD60A', '#FF6B00', '#A855F7']

export const DIVERGING = [
  { name: 'Heat / Cool', stops: ['#00C7FF', '#0a0a0a', '#FF3B30'] },
  { name: 'Spectral',    stops: ['#3B82F6', '#FACC15', '#EF4444'] },
  { name: 'Earth',       stops: ['#1F5F3F', '#E8DDB5', '#7A3F2A'] },
]

export const RAMPS = [
  { name: 'Viridis',  stops: ['#440154', '#3B528B', '#21908C', '#5DC863', '#FDE725'] },
  { name: 'Inferno',  stops: ['#000004', '#4B0C6B', '#D44842', '#FCA50A', '#FCFFA4'] },
  { name: 'Terrain',  stops: ['#2E5A3E', '#A8C97A', '#E5C77A', '#B07050', '#FFFFFF'] },
  { name: 'Slope',    stops: ['#1A9850', '#A6D96A', '#FEE08B', '#F46D43', '#A50026'] },
]

// AWS Terrarium encoding (elevation-tiles-prod)
export function decodeElevation(r, g, b) {
  return (r * 256 + g + b / 256) - 32768 // meters
}

// 0-9 band index, or -1 if out of range
export function slopeToBandIndex(slope) {
  for (let i = 0; i < SLOPE_BANDS.length; i++) {
    const { lo, hi } = SLOPE_BANDS[i]
    if (slope >= lo && slope < hi) return i
  }
  return -1
}

// 0-7 aspect sector index (N=0, NE=1, ..., NW=7)
export function aspectToSectorIndex(aspectDeg) {
  return Math.floor(((aspectDeg + 22.5) % 360) / 45)
}

function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

export function interpolateColor(stops, t) {
  if (stops.length === 1) return hexToRgb(stops[0])
  const scaled = t * (stops.length - 1)
  const lo = Math.floor(scaled)
  const hi = Math.min(lo + 1, stops.length - 1)
  const f = scaled - lo
  const [r0, g0, b0] = hexToRgb(stops[lo])
  const [r1, g1, b1] = hexToRgb(stops[hi])
  return [
    Math.round(r0 + (r1 - r0) * f),
    Math.round(g0 + (g1 - g0) * f),
    Math.round(b0 + (b1 - b0) * f),
  ]
}

export function getColorStops(filters) {
  const { colorMode, palette, solid } = filters
  if (colorMode === 'solid') return [solid]
  if (colorMode === 'diverging') return DIVERGING[palette.index]?.stops || DIVERGING[0].stops
  return RAMPS[palette.index]?.stops || RAMPS[0].stops
}

// Decode raw RGBA pixel data into a Float32Array elevation grid (meters).
export function decodeElevationGrid(pixels) {
  const elev = new Float32Array(256 * 256)
  for (let i = 0; i < 256 * 256; i++) {
    const base = i * 4
    elev[i] = decodeElevation(pixels[base], pixels[base + 1], pixels[base + 2])
  }
  return elev
}

// Returns null (outside viewshed), true (visible), or false (hidden).
function sampleViewshed(viewshed, lng, lat) {
  const { visible, gridWidth, gridHeight, bounds, obsGridX, obsGridY, maxDistPx } = viewshed
  const { west, east, north, south } = bounds
  if (lng < west || lng > east || lat < south || lat > north) return null

  // bounds are the grid's tile bounds: longitude is linear across them, latitude is Web Mercator.
  const mercY = (latDeg) => Math.log(Math.tan(Math.PI / 4 + latDeg * Math.PI / 360))
  const gx = Math.max(0, Math.min(gridWidth  - 1, ((lng - west) / (east - west) * gridWidth) | 0))
  const gy = Math.max(0, Math.min(gridHeight - 1, ((mercY(north) - mercY(lat)) / (mercY(north) - mercY(south)) * gridHeight) | 0))
  const dx = gx - obsGridX, dy = gy - obsGridY
  if (dx * dx + dy * dy > maxDistPx * maxDistPx) return null

  return visible[gy * gridWidth + gx] === 1
}

// Compute slope/aspect for every pixel and return a colored RGBA Uint8ClampedArray.
// elev: Float32Array from decodeElevationGrid (256×256)
// z: tile zoom, tileX/tileY: tile column/row indices
// filters: { slopeBands, aspects, elevMinM, elevMaxM, colorMode, palette, solid, opacity }
// viewshed (optional): { visible, gridWidth, gridHeight, bounds, obsGridX, obsGridY, maxDistPx, opacity }
export function computeTerrainTile(elev, z, tileX, tileY, filters, viewshed = null) {
  const SIZE = 256
  const n = Math.pow(2, z)
  const tileLat = Math.atan(Math.sinh(Math.PI * (1 - 2 * (tileY + 0.5) / n)))
  // Meters per pixel at this zoom and latitude
  const pixelSize = (2 * Math.PI * 6371000 * Math.cos(tileLat)) / (Math.pow(2, z) * SIZE)

  // Precompute tile geographic bounds for per-pixel viewshed sampling
  let tileLng0, tileLng1, tileNorth, tileSouth
  if (viewshed) {
    tileLng0  = tileX / n * 360 - 180
    tileLng1  = (tileX + 1) / n * 360 - 180
    tileNorth = Math.atan(Math.sinh(Math.PI * (1 - 2 * tileY / n)))       * 180 / Math.PI
    tileSouth = Math.atan(Math.sinh(Math.PI * (1 - 2 * (tileY + 1) / n))) * 180 / Math.PI
  }

  const { slopeBands, aspects, elevMinM, elevMaxM } = filters
  const stops = getColorStops(filters)
  const out = new Uint8ClampedArray(SIZE * SIZE * 4)

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const idx = y * SIZE + x

      // Central differences with edge clamping
      const xm = x > 0 ? x - 1 : x
      const xp = x < SIZE - 1 ? x + 1 : x
      const ym = y > 0 ? y - 1 : y
      const yp = y < SIZE - 1 ? y + 1 : y
      const dxScale = (xp - xm) * pixelSize
      const dyScale = (yp - ym) * pixelSize
      const dzdx = dxScale > 0 ? (elev[y * SIZE + xp] - elev[y * SIZE + xm]) / dxScale : 0
      const dzdy = dyScale > 0 ? (elev[yp * SIZE + x] - elev[ym * SIZE + x]) / dyScale : 0

      const slope = Math.atan(Math.sqrt(dzdx * dzdx + dzdy * dzdy)) * (180 / Math.PI)
      // Aspect: the compass bearing the slope FACES (its downhill direction), 0=N clockwise.
      //
      // Taken directly from the downhill vector in (east, north) components — east is -dzdx,
      // north is +dzdy because grid y increases southward — so atan2(east, north) is already
      // a bearing and needs no conversion.
      //
      // The previous form, (atan2(-dzdy, dzdx) + 450) % 360, converted a maths angle
      // (anticlockwise from east) to a bearing by ADDING 90 where the conversion requires
      // 90 - a. That is a reflection about the north–south axis: north and south stayed
      // correct while east and west were swapped, so the output looked entirely plausible
      // — smooth, well distributed, correctly shaped — while pointing at the opposite
      // hillsides for anything with an east–west component. For hunting that is the
      // distinction that matters most: an east face takes the morning sun, a west face
      // holds afternoon heat, and they drive different bedding and thermals.
      const aspectRaw = (Math.atan2(-dzdx, dzdy) * (180 / Math.PI) + 360) % 360
      const elevation = elev[idx]

      const bandIdx = slopeToBandIndex(slope)
      const sectorIdx = aspectToSectorIndex(aspectRaw)

      const matches =
        bandIdx !== -1 &&
        slopeBands.includes(bandIdx) &&
        aspects.includes(sectorIdx) &&
        elevation >= elevMinM &&
        elevation <= elevMaxM

      const base = idx * 4
      if (matches) {
        // In combined mode, check viewshed visibility for this pixel.
        if (viewshed) {
          const pixLng = tileLng0 + (x + 0.5) / SIZE * (tileLng1 - tileLng0)
          const pixLat = tileNorth - (y + 0.5) / SIZE * (tileNorth - tileSouth)
          const vis = sampleViewshed(viewshed, pixLng, pixLat)
          if (vis === false) {
            // Within viewshed radius and hidden → black
            out[base + 3] = viewshed.opacity
            continue
          }
          // vis === null (outside radius) or true (visible) → render terrain color normally
        }

        let t
        const { colorBy, elevMinM, elevMaxM } = filters
        if (colorBy === 'aspect') {
          t = sectorIdx / 7
        } else if (colorBy === 'elevation') {
          const range = elevMaxM - elevMinM
          t = range > 0 ? Math.max(0, Math.min(1, (elevation - elevMinM) / range)) : 0.5
        } else {
          t = Math.min(slope / 45, 1)
        }
        const [r, g, b] = interpolateColor(stops, t)
        out[base]     = r
        out[base + 1] = g
        out[base + 2] = b
        out[base + 3] = 220
      } else {
        out[base + 3] = 0
      }
    }
  }

  return out
}

// Tile center latitude in radians from tile y/z
export function tileCenterLat(y, z) {
  const n = Math.PI - (2 * Math.PI * (y + 0.5)) / Math.pow(2, z)
  return Math.atan(Math.sinh(n))
}

// Diagnostic: compute slope distribution for a tile without filtering/coloring
export function slopeStats(elev, z, tileLat) {
  const SIZE = 256
  const pixelSize = (2 * Math.PI * 6371000 * Math.cos(tileLat)) / (Math.pow(2, z) * SIZE)

  const slopes = new Float32Array(SIZE * SIZE)
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const xm = x > 0 ? x - 1 : x, xp = x < SIZE - 1 ? x + 1 : x
      const ym = y > 0 ? y - 1 : y, yp = y < SIZE - 1 ? y + 1 : y
      const dxScale = (xp - xm) * pixelSize, dyScale = (yp - ym) * pixelSize
      const dzdx = dxScale > 0 ? (elev[y * SIZE + xp] - elev[y * SIZE + xm]) / dxScale : 0
      const dzdy = dyScale > 0 ? (elev[yp * SIZE + x] - elev[ym * SIZE + x]) / dyScale : 0
      slopes[y * SIZE + x] = Math.atan(Math.sqrt(dzdx * dzdx + dzdy * dzdy)) * (180 / Math.PI)
    }
  }

  const sorted = Array.from(slopes).sort((a, b) => a - b)
  const n = sorted.length
  const mean = sorted.reduce((s, v) => s + v, 0) / n
  const elevArr = Array.from(elev)
  const bandCounts = SLOPE_BANDS.map(b => sorted.filter(s => s >= b.lo && s < b.hi).length)

  return {
    pixelSizeM: +pixelSize.toFixed(2),
    elev: { minM: +Math.min(...elevArr).toFixed(1), maxM: +Math.max(...elevArr).toFixed(1) },
    slope: {
      min:    +sorted[0].toFixed(2),
      p10:    +sorted[Math.floor(n * 0.10)].toFixed(2),
      p25:    +sorted[Math.floor(n * 0.25)].toFixed(2),
      median: +sorted[Math.floor(n * 0.50)].toFixed(2),
      p75:    +sorted[Math.floor(n * 0.75)].toFixed(2),
      p90:    +sorted[Math.floor(n * 0.90)].toFixed(2),
      max:    +sorted[n - 1].toFixed(2),
      mean:   +mean.toFixed(2),
    },
    bands: Object.fromEntries(SLOPE_BANDS.map((b, i) => [
      b.label || `${b.lo}-${b.hi}°`,
      `${bandCounts[i]} px (${(bandCounts[i] / n * 100).toFixed(1)}%)`,
    ])),
  }
}
